import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ProductsPage from "@/pages/ProductsPage";
import ProductComparisonPage from "@/pages/ProductComparisonPage";
import SuppliersPage from "@/pages/SuppliersPage";
import OrdersPage from "@/pages/OrdersPage";
import InventoryPage from "@/pages/InventoryPage";
import InventoryFormsPage from "@/pages/InventoryFormsPage";
// ConsumptionPage integrated into InventoryPage (Stock) as a tab
import RecipesPage from "@/pages/RecipesPage";
import SitesPage from "@/pages/SitesPage";
import ReceptionStatsPage from "@/pages/ReceptionStatsPage";
import FinancePage from "@/pages/FinancePage";
import SalesPage from "@/pages/SalesPage";
import MyFoodSalesPage from "@/pages/MyFoodSalesPage";
import ConceptLabSalesPage from "@/pages/ConceptLabSalesPage";
import ProductionPage from "@/pages/ProductionPage";
import RatiosPage from "@/pages/RatiosPage";
import LossesPage from "@/pages/LossesPage";
import UsersPage from "@/pages/UsersPage";
import RolePermissionsPage from "@/pages/RolePermissionsPage";
import CompliancePage from "@/pages/CompliancePage";
import SyncPage from "@/pages/SyncPage";
import PhoneCapturePage from "@/pages/PhoneCapturePage";
// ConsolidationPage integrated into OrdersPage as a tab
// TransfersPage integrated into OrdersPage as a tab
import Layout from "@/components/Layout";

// Guide du Sushiman
import GuideLayout from "@/pages/guide/GuideLayout";
import GuideLoginPage from "@/pages/guide/GuideLoginPage";
import GuideHomePage from "@/pages/guide/GuideHomePage";
import GuideEntreprisePage from "@/pages/guide/GuideEntreprisePage";
import GuideCornersPage from "@/pages/guide/GuideCornersPage";
import GuideRecettesPage from "@/pages/guide/GuideRecettesPage";
import GuideHaccpPage from "@/pages/guide/GuideHaccpPage";
import GuideHaccpStandPage from "@/pages/guide/GuideHaccpStandPage";
import GuideChecklistIndexPage from "@/pages/guide/GuideChecklistIndexPage";
import GuideChecklistArriveePage from "@/pages/guide/GuideChecklistArriveePage";
import GuideChecklistDepartPage from "@/pages/guide/GuideChecklistDepartPage";
import GuideChecklistResultsPage from "@/pages/guide/GuideChecklistResultsPage";
import GuideSiteSelectPage from "@/pages/guide/GuideSiteSelectPage";
import GuideEvaluationsPage from "@/pages/guide/GuideEvaluationsPage";

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Mot de passe provisoire : l'écran de changement (LoginPage) est obligatoire
    if (user.must_change_password) {
        return <Navigate to="/login" replace />;
    }

    if (user.guide_only) {
        return <Navigate to="/guide" replace />;
    }
    
    return children;
}

function GuideProtectedRoute({ children }) {
    const token = localStorage.getItem('guide_token');
    if (!token) {
        return <Navigate to="/guide/login" replace />;
    }
    return children;
}

function HomeRoute() {
    const { hasPermission } = useAuth();
    if (hasPermission('dashboard_view')) {
        return <DashboardPage />;
    }
    // Redirect to the first page the user can access
    const fallbacks = [
        ['/orders', 'orders_view'],
        ['/pertes', 'losses_entry'],
        ['/inventory', 'inventories_view'],
        ['/products', 'products_view'],
        ['/suppliers', 'suppliers_view'],
        ['/recipes', 'recipes_view'],
        ['/finance', 'finance_view'],
        ['/reception-stats', 'stats_view'],
        ['/sales', 'sales_entries_view'],
        ['/sites', 'sites_edit'],
        ['/users', 'users_view'],
    ];
    const target = fallbacks.find(([, perm]) => hasPermission(perm));
    if (target) return <Navigate to={target[0]} replace />;
    return <DashboardPage />;
}

function AppRoutes() {
    const { user } = useAuth();
    
    return (
        <Routes>
            <Route path="/login" element={(user && !user.must_change_password) ? <Navigate to="/" replace /> : <LoginPage />} />
            <Route path="/capture/:sessionId" element={<PhoneCapturePage />} />
            <Route path="/" element={
                <ProtectedRoute>
                    <Layout>
                        <HomeRoute />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/products" element={
                <ProtectedRoute>
                    <Layout>
                        <ProductsPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/products/comparison" element={
                <ProtectedRoute>
                    <Layout>
                        <ProductComparisonPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/suppliers" element={
                <ProtectedRoute>
                    <Layout>
                        <SuppliersPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/orders" element={
                <ProtectedRoute>
                    <Layout>
                        <OrdersPage />
                    </Layout>
                </ProtectedRoute>
            } />
// ConsolidationPage integrated into OrdersPage as a tab
            <Route path="/consolidation" element={<Navigate to="/orders" replace />} />
            <Route path="/transfers" element={<Navigate to="/orders" replace />} />
            <Route path="/inventory" element={
                <ProtectedRoute>
                    <Layout>
                        {(user?.role === 'admin' || (user?.permissions || []).includes('stock_page_view'))
                            ? <InventoryPage />
                            : <div className="p-8 text-center text-slate-500" data-testid="stock-access-denied">Vous n'avez pas accès à la page Stock.</div>}
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/consumption" element={<Navigate to="/inventory" replace />} />
            <Route path="/inventory-forms" element={
                <ProtectedRoute>
                    <Layout>
                        <InventoryFormsPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/recipes" element={
                <ProtectedRoute>
                    <Layout>
                        <RecipesPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/reception-stats" element={
                <ProtectedRoute>
                    <Layout>
                        <ReceptionStatsPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/finance" element={
                <ProtectedRoute>
                    <Layout>
                        <FinancePage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/sales" element={
                <ProtectedRoute>
                    <Layout>
                        <SalesPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/sales/my-food" element={
                <ProtectedRoute>
                    <Layout>
                        <MyFoodSalesPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/sales/concept-lab" element={
                <ProtectedRoute>
                    <Layout>
                        <ConceptLabSalesPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/production" element={
                <ProtectedRoute>
                    <Layout>
                        <ProductionPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/ratios" element={
                <ProtectedRoute>
                    <Layout>
                        <RatiosPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/pertes" element={
                <ProtectedRoute>
                    <Layout>
                        <LossesPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/sites" element={
                <ProtectedRoute>
                    <Layout>
                        <SitesPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/users" element={
                <ProtectedRoute>
                    <Layout>
                        <UsersPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/permissions" element={
                <ProtectedRoute>
                    <Layout>
                        <RolePermissionsPage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/conformite" element={
                <ProtectedRoute>
                    <Layout>
                        <CompliancePage />
                    </Layout>
                </ProtectedRoute>
            } />
            <Route path="/sync" element={
                <ProtectedRoute>
                    <Layout>
                        <SyncPage />
                    </Layout>
                </ProtectedRoute>
            } />

            {/* Guide du Sushiman */}
            <Route path="/guide/login" element={<GuideLoginPage />} />
            <Route path="/guide" element={
                <GuideProtectedRoute>
                    <GuideLayout />
                </GuideProtectedRoute>
            }>
                <Route index element={<GuideHomePage />} />
                <Route path="select-site" element={<GuideSiteSelectPage />} />
                <Route path="entreprise" element={<GuideEntreprisePage />} />
                <Route path="corners" element={<GuideCornersPage />} />
                <Route path="recettes" element={<GuideRecettesPage />} />
                <Route path="haccp" element={<GuideHaccpPage />} />
                <Route path="haccp/stand" element={<GuideHaccpStandPage />} />
                <Route path="checklist" element={<GuideChecklistIndexPage />} />
                <Route path="checklist/arrivee" element={<GuideChecklistArriveePage />} />
                <Route path="checklist/depart" element={<GuideChecklistDepartPage />} />
                <Route path="checklist/resultats" element={<GuideChecklistResultsPage />} />
                <Route path="evaluations" element={<GuideEvaluationsPage />} />
            </Route>

            <Route path="*" element={
                localStorage.getItem('guide_token') && !localStorage.getItem('token')
                    ? <Navigate to="/guide" replace />
                    : <Navigate to="/" replace />
            } />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppRoutes />
                <Toaster position="top-right" richColors />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
